import React, { useState } from "react";
import { useTable } from "@/lib/store";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Chip, Empty, Modal, Field, Area } from "@/components/kit";
import { ROMAN, fmtDate } from "@/lib/util";
import { DateField } from "@/components/DateField";

export default function ParkingLot() {
  const { parking, meetings, memberById, yearId, refresh, canWrite, me } = useTable();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ topic: "", whyParked: "", raisedAtMeeting: "", revisitAt: "" });

  const add = async () => {
    try {
      await api.post("/parking", { ...form, yearId });
      setOpen(false); setForm({ topic: "", whyParked: "", raisedAtMeeting: "", revisitAt: "" });
      await refresh();
      toast.success("Parked, not dropped.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="parking-page">
      <PageHead
        kicker="Carried forward"
        title="Parking Lot"
        sub="Raised but deferred — carried forward, never dropped."
        right={canWrite && <button className="btn-ink" data-testid="add-parking" onClick={() => setOpen(true)}>Park a topic</button>}
      />

      {parking.length === 0 ? (
        <Empty line="Nothing parked. Everything raised has been met." testId="parking-empty" />
      ) : (
        <div className="space-y-3">
          {parking.map((p) => {
            const by = memberById(p.raisedById);
            const canEdit = canWrite && (me.isAdmin || p.raisedById === me.memberId);
            return (
              <Card key={p.id} data-testid={`parking-${p.id}`}>
                <h3 className="serif text-xl leading-snug">{p.topic}</h3>
                {p.whyParked && <p className="text-sm mt-2" style={{ color: "var(--ink-mute)" }}>{p.whyParked}</p>}
                <div className="flex gap-2 mt-3 flex-wrap items-center">
                  <Chip>{by?.name || "—"}</Chip>
                  {p.raisedAtMeeting && <Chip>Raised at {p.raisedAtMeeting}</Chip>}
                  {p.revisitAt && <Chip>Revisit {fmtDate(p.revisitAt)}</Chip>}
                  {canEdit ? (
                    <select
                      className="field !w-auto !py-1 !text-xs"
                      data-testid={`parking-status-${p.id}`}
                      value={p.status}
                      onChange={async (e) => { await api.put(`/parking/${p.id}`, { status: e.target.value }); await refresh(); }}
                    >
                      {["Open", "Scheduled", "Closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <Chip>{p.status}</Chip>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Park a topic" testId="parking-modal">
        <Field label="Topic" value={form.topic} data-testid="parking-topic" onChange={(e) => setForm({ ...form, topic: e.target.value })} />
        <Area label="Why it was parked" value={form.whyParked} onChange={(e) => setForm({ ...form, whyParked: e.target.value })} />
        <label className="block">
          <span className="kicker block mb-1.5">Raised at</span>
          <select className="field" value={form.raisedAtMeeting} onChange={(e) => setForm({ ...form, raisedAtMeeting: e.target.value })}>
            <option value="">—</option>
            {meetings.map((m) => <option key={m.id} value={`Chapter ${ROMAN[m.num - 1]}`}>Chapter {ROMAN[m.num - 1]}</option>)}
          </select>
        </label>
        <DateField label="Revisit at" value={form.revisitAt} testId="parking-revisit" onChange={(v) => setForm({ ...form, revisitAt: v })} />
        <button className="btn-ink w-full" data-testid="parking-save" onClick={add}>Park it</button>
      </Modal>
    </div>
  );
}
